/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { DecimalPipe } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { first } from 'rxjs/operators';
import { Cycle } from '@src/app/model/cycle';
import { PurchaseDocument, purchaseDocsFolderName } from '@src/app/model/purchaseDocument';
import { PurchaseService } from '@src/app/modules/txn/purchase.service';
import { DialogBannerComponent } from '@src/app/shared/dialogs/dialog-banner-component';
import { MessageService } from '@src/app/shared/message.service';
import { PurchaseDocContents } from '@src/app/modules/budget/cycle-page/purchase-docs/purchaseDocContents';
import { PurchaseDocFolder } from '@src/app/modules/budget/cycle-page/purchase-docs/purchaseDocFolder';

type DocsPhase = 'loading' | 'confirm' | 'running' | 'done';

/**
 * Downloads every purchase document in a cycle into a folder the user chooses.
 *
 * Nothing is zipped and nothing is stored on our side - the files already exist, and a zip of
 * them would be a second copy we then have to keep. The browser pulls them one at a time and
 * writes them straight to disk, so a re-run after a correction costs nothing but the time.
 *
 * Files are laid out as <type>/<supplier>/<refNr>-NN.pdf, and _contents.csv at the top says what
 * should be there. Written one at a time and released as we go: holding four hundred invoices in
 * memory to write them all at the end is what would make a big cycle fall over.
 */
@Component({
    selector: 'app-purchase-docs',
    imports: [MatDialogModule, MatButtonModule, MatProgressBarModule, MatIconModule,
        DialogBannerComponent, DecimalPipe],
    templateUrl: './purchase-docs.component.html',
})
export class PurchaseDocsComponent implements OnInit {
    dialogRef = inject(MatDialogRef<PurchaseDocsComponent>);
    data = inject(MAT_DIALOG_DATA);
    purchaseSvc = inject(PurchaseService);
    msgSvc = inject(MessageService);

    cycle: Cycle = this.data.cycle;
    phase: DocsPhase = 'loading';

    /** Named up front so the confirmation can say where the files are about to go. */
    targetFolderName = purchaseDocsFolderName(this.cycle.teamId, this.cycle.name);

    rows: PurchaseDocument[] = [];
    /** The rows that actually have a file behind them - what the progress bar counts. */
    documents: PurchaseDocument[] = [];
    missing: PurchaseDocument[] = [];

    written = 0;
    failed: PurchaseDocument[] = [];
    currentFile = '';

    private folder: PurchaseDocFolder;
    private unloadGuard = (event: BeforeUnloadEvent) => event.preventDefault();

    ngOnInit(): void {
        this.purchaseSvc.getCycleDocuments(this.cycle.id).pipe(first()).subscribe(rows => {
            this.rows = rows;
            this.documents = rows.filter(row => row.hasDocument);
            this.missing = rows.filter(row => !row.hasDocument);
            this.phase = 'confirm';
        });
    }

    get transactionCount(): number {
        return new Set(this.rows.map(row => row.txnId)).size;
    }

    /**
     * Type and supplier combined, because that is the pair a folder is created for. Counted off
     * the documents, not the transactions - a supplier whose invoices are all missing never gets
     * a folder, so promising one would be a lie.
     */
    get folderCount(): number {
        return new Set(this.documents.map(row => row.folder)).size;
    }

    /** The folder we made - see PurchaseDocFolder.name for why there is no full path. */
    get folderName(): string {
        return this.folder?.name ?? '';
    }

    /** The folder the user picked, which the one above was created inside. */
    get pickedName(): string {
        return this.folder?.pickedName ?? '';
    }

    get progress(): number {
        const done = this.written + this.failed.length;

        return this.documents.length === 0 ? 0 : (done / this.documents.length) * 100;
    }

    /**
     * The picker has to be opened from the click itself - a folder handle cannot be asked for
     * without a user gesture, so this cannot be moved behind a confirmation.
     */
    async start(): Promise<void> {
        try {
            this.folder = await PurchaseDocFolder.pick(this.targetFolderName);
        } catch (e) {
            console.error('Could not open the folder picker', e);
            this.msgSvc.show($localize`Could not open the folder picker - please try again`);

            return;
        }
        if (!this.folder) {
            return; // Cancelled the picker - leave the dialog as it was.
        }
        this.written = 0;
        this.failed = [];
        await this.run(this.documents);
    }

    /** Retries only what failed, into the folder already chosen - no second picker. */
    async retryFailed(): Promise<void> {
        const retrying = this.failed;
        this.failed = [];
        await this.run(retrying);
    }

    private async run(documents: PurchaseDocument[]): Promise<void> {
        this.startRunning();
        try {
            await this.writeAll(documents);
            await this.writeContents();
        } finally {
            this.stopRunning();
        }
    }

    private async writeAll(documents: PurchaseDocument[]): Promise<void> {
        for (const document of documents) {
            this.currentFile = `${document.folder}/${document.saveAs}`;
            try {
                await this.folder.write(document.folder, document.saveAs, await this.fetch(document));
                this.written = this.written + 1;
            } catch (e) {
                console.error(`Failed to save ${this.currentFile}`, e);
                this.failed.push(document);
            }
        }
        this.currentFile = '';
    }

    private async fetch(document: PurchaseDocument): Promise<Blob> {
        const response = await this.purchaseSvc.getAttachment(document.attachmentId)
            .pipe(first()).toPromise();

        return new Blob([response.body], { type: response.headers.get('our-mime-type') });
    }

    /** Written last, so it reports what actually landed rather than what was hoped for. */
    private async writeContents(): Promise<void> {
        const failedIds = new Set(this.failed.map(document => document.attachmentId));
        const contents = new PurchaseDocContents(this.cycle, this.rows, failedIds).build();
        try {
            await this.folder.write('', PurchaseDocContents.FILE_NAME, contents);
        } catch (e) {
            console.error('Failed to write the contents file', e);
        }
    }

    /** A run that is interrupted leaves a half filled folder, so make leaving deliberate. */
    private startRunning(): void {
        this.phase = 'running';
        this.dialogRef.disableClose = true;
        window.addEventListener('beforeunload', this.unloadGuard);
    }

    private stopRunning(): void {
        this.phase = 'done';
        this.dialogRef.disableClose = false;
        window.removeEventListener('beforeunload', this.unloadGuard);
    }
}
